import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import {Link} from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import { Link as MuiLink} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Infinity from "../../assets/images/infinityLogo.png";
import GoogleIcon from '@mui/icons-material/Google';

import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
} from "firebase/auth";
import { app, db} from "../../firebase";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { toast } from "react-toastify";

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.primary" align="center" {...props}>
      {'Copyright © Infinity Adda '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

export default function SignUpSide() {
  const navigate = useNavigate();
  const [otpDialog, setOtpDialog] = React.useState(false);
  const [pendingUser, setPendingUser] = React.useState(null);
  const [isResending, setIsResending] = React.useState(false);

  const handleGoogleSignUp = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Save user data to Firestore
      await setDoc(doc(db, "users", user.uid), {
        name: user.displayName,
        email: user.email,
        timestamp: serverTimestamp(),
      });
      
      toast.success("Google signup successful!");
      navigate("/home");
    } catch (error) {
      console.error("Error with Google signup:", error.message);
      toast.error("Google signup failed. Please try again.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const { name, email, password, confirmPassword } = Object.fromEntries(formData);
  
    try {
      if(password !== confirmPassword) {
        toast.error("Passwords do not match. Please try again.")
        return;
      }
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(auth.currentUser, { displayName: name });
      const user = userCredential.user;
      
      // Save user data to Firestore
      const formDataCopy = { name, email };
      formDataCopy.timestamp = serverTimestamp();
      await setDoc(doc(db, "users", user.uid), formDataCopy);
      
      // Email verification is now mandatory for email signup
      await sendEmailVerification(user);
      setPendingUser(user);
      setOtpDialog(true);
      toast.info("Account created! Verification email sent. Please check your inbox to complete registration.");
  
    } catch (error) {
      console.error("Error creating user:", error.message);
      toast.error("Signup failed. Please try again.")
    }
  };

  const handleEmailVerification = async () => {
    try {
      // Reload user to check verification status
      await pendingUser.reload();
      
      if (pendingUser.emailVerified) {
        setOtpDialog(false);
        toast.success("Email verified successfully! Welcome to Infinity Adda!");
        navigate("/home");
      } else {
        toast.error("Email not verified yet. Please check your inbox and click the verification link, then try again.");
      }
    } catch (error) {
      console.error("Error checking email verification:", error.message);
      toast.error("Verification failed. Please try again.");
    }
  };

  const resendVerificationEmail = async () => {
    setIsResending(true);
    try {
      if (pendingUser) {
        await sendEmailVerification(pendingUser);
        toast.info("Verification email resent! Please check your inbox.");
      }
    } catch (error) {
      console.error("Error resending verification email:", error.message);
      toast.error("Failed to resend verification email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleCancelVerification = () => {
    setOtpDialog(false);
    setPendingUser(null);
    toast.info("Registration cancelled. You can try signing up again.");
  };

  return (
    <>
      <Grid container component="main" sx={{ height: "100vh", my:"auto", marginTop: "60px"}}>
        <CssBaseline />

        <Grid container
            sx={{
              maxWidth: 450,
              margin:"auto",
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
        >
          <Box
            sx={{
              borderRadius: '10px',
              boxShadow: 24,
              padding: 4,
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: 'text.primary' , margin: "auto"}}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5" align='center'>
              Sign Up
            </Typography>
            
            {/* Google Sign Up Button */}
            <Button
              fullWidth
              variant="outlined"
              onClick={handleGoogleSignUp}
              startIcon={<GoogleIcon />}
              sx={{ 
                mt: 2, 
                mb: 2,
              }}
            >
              Continue with Google
            </Button>
            
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Name"
                name="name"
                autoComplete="name"
                autoFocus
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="new-password"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
              />
              
              {/* Information about email verification */}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                📧 Email verification is required to complete your registration and secure your account.
              </Typography>
              
              <Typography variant="body2" mt={2}>
                By signing up, you agree to our{' '}
                <Link to="/policies" variant="body2">
                  <MuiLink>
                    Terms and Conditions
                  </MuiLink>
                </Link>
              </Typography>
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Create Account
              </Button>
              
              <Grid container>
                <Grid item>
                  <Link to="/signin" variant="body2" style={{fontSize: "0.8em"}}>
                    <MuiLink>
                      {"Already have an account? Sign In"}  
                    </MuiLink>
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Email Verification Dialog */}
      <Dialog 
        open={otpDialog} 
        onClose={handleCancelVerification} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown={false}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          📧 Verify Your Email
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
            We've sent a verification link to your email address.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Please check your inbox (and spam folder) and click the verification link to activate your account.
          </Typography>
          <Typography variant="body2" color="primary" sx={{ textAlign: 'center', fontWeight: 'medium' }}>
            After clicking the verification link, return here and click "I've Verified My Email".
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 3 }}>
          <Button 
            onClick={resendVerificationEmail} 
            color="secondary"
            disabled={isResending}
          >
            {isResending ? "Sending..." : "Resend Email"}
          </Button>
          <Button onClick={handleCancelVerification} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleEmailVerification} 
            variant="contained" 
            color="primary"
          >
            I've Verified My Email
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}